import React from 'react';
import { TFunction } from 'i18next';
import { NavLink } from 'react-router-dom';
import _get from 'lodash/get';
import { InterventionCategory } from 'graphql-common';
import TextCell from '@lib/components/Table/commonComponents/TextCell/TextCell';
import ChipCell from '@lib/components/Table/commonComponents/ChipCell/ChipCell';
import { CellProps, Column, TableAction } from '@lib/components/Table/types';
import { ChipTypes } from '@lib/components/Chip/Chip';
import { IconButtonTypes } from '@lib/components/IconButton/IconButton';
import LinkComponent from '@lib/components/Link/Link';
import CollapseCell from '@lib/components/Table/commonComponents/CollapseCell/CollapseCell';
import { APP_URLS } from 'constants/urls';

type ColumnsArgs = {
  onEditHandler: (v: InterventionCategory) => void;
  t: TFunction<'translation', undefined>;
  isInterventionCategoriesViewOnly?: boolean;
  isInterventionsLocked?: boolean;
};

export const getColumns = ({
  onEditHandler,
  t,
  isInterventionCategoriesViewOnly,
  isInterventionsLocked,
}: ColumnsArgs) => {
  const result: Column<InterventionCategory>[] = [
    {
      id: 'name',
      Header: t('name'),
      accessor: 'name',
      Cell: ({ item }: CellProps<InterventionCategory>) => {
        const label = _get(item, 'name');
        const type = _get(item, 'color') as ChipTypes;
        if (!label || !type) return '-';
        return (
          <ChipCell
            label={label}
            type={type}
            leftIcon="circle"
            onClick={
              isInterventionCategoriesViewOnly
                ? undefined
                : () => onEditHandler(item)
            }
          />
        );
      },
      maxWidth: 300,
      minWidth: 250,
      sortable: true,
    },
    {
      id: 'description',
      Header: t('description'),
      accessor: 'description',
      Cell: (props: CellProps<InterventionCategory>) => (
        <CollapseCell {...props} minHeight={20} />
      ),
      maxWidth: 300,
      minWidth: 250,
    },
  ];
  if (!isInterventionsLocked) {
    result.push(
      {
        id: 'visibleInterventionsCount',
        Header: t('interventions'),
        accessorFn: ({ item }: CellProps<InterventionCategory>) =>
          item.visibleInterventionsCount || '0',
        Cell: (props: CellProps<InterventionCategory>) => (
          <TextCell {...props} />
        ),
        maxWidth: 170,
        minWidth: 170,
        width: 170,
        sortable: true,
      },
      {
        id: 'linkedInterventions',
        Header: t('linked-interventions'),
        accessorFn: ({ item }: CellProps<InterventionCategory>) => {
          const { interventions } = item;
          return interventions.collection.map(({ id, name }) => (
            <LinkComponent
              key={id}
              to={APP_URLS.dashboard.interventions.view.getDynamicPath({
                pathParts: { id },
              })}
              component={NavLink}
            >
              {name}
            </LinkComponent>
          ));
        },
        Cell: (props: CellProps<InterventionCategory>) => (
          <CollapseCell {...props} minHeight={20} />
        ),
        maxWidth: 300,
        minWidth: 250,
      },
    );
  }
  return result;
};

type ActionsMenuArgs = {
  onEditHandler: (v: InterventionCategory) => void;
  onDelete: (v: InterventionCategory) => void;
  t: TFunction<'translation', undefined>;
  isInterventionCategoriesViewOnly: boolean;
};

export function getActions({
  onEditHandler,
  onDelete,
  isInterventionCategoriesViewOnly,
  t,
}: ActionsMenuArgs) {
  const result: TableAction<InterventionCategory>[] = [];
  if (!isInterventionCategoriesViewOnly) {
    result.push(
      {
        id: 'edit-action',
        icon: 'edit',
        onClick: (item: InterventionCategory) => onEditHandler(item),
        type: IconButtonTypes.PrimaryOutlined,
      },
      {
        id: 'delete-action',
        icon: 'delete',
        onClick: (item: InterventionCategory) => onDelete(item),
        type: IconButtonTypes.PrimaryOutlined,
        predictorFnDisable: (item: InterventionCategory) =>
          !item.canDestroy.value,
        predictorFnDisableTooltipProps: () => ({
          body: t('cannot-delete-intervention-category'),
          placement: 'left',
          maxWidth: 150,
        }),
      },
    );
  }
  return result;
}
